import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import httpStatus from 'http-status';

import { AOLIntegrationAPI } from '~/api/aol-integration-api';
import { APIParameters } from '~/api/generic-api';
import { NavigationAPI } from '~/api/navigation-api';
import { AppRoutes, FORM_SUBTYPE } from '~/app-routes';
import { ContextMenuItem } from '~/components/context-menu-list/context-menu-list';
import {
	NavigationEntity,
	NavigationMenuItem,
	NavigationViewStructure,
} from '~/components/navigation/navigation-types';
import {
	computeNavigationName,
	computeNavURL,
	filterStatefulQueryParameters,
	getNavigationMenuItemsOfView,
} from '~/components/navigation/navigation-utils';
import { Notification } from '~/components/notification/notification';
import { CommonURLParam } from '~/constants';
import { EntityType } from '~/models/entities/entity-type';
import { ReplaceConfigLabelStep } from '~/pipelines/replace-config-label-step';
import { SettingsState } from '~/settings-state/settings-state';
import { FetchUtils } from '~/utils/fetch-utils';
import { Logger } from '~/utils/logger';

@autoinject
export class NavigationState {
	public viewNameList: string[] = [];

	public structure: {
		[viewName: string]: NavigationViewStructure | undefined;
	} = {};

	/**
	 * Form names of those forms that have form subtype
	 * These form names should get transformation on navigation data fetch. The delimiter
	 * between base form name and form subtype is transformed from '-' to '/'.
	 */
	private readonly formWithFormSubType: readonly string[];

	constructor(
		private readonly navigationAPI: NavigationAPI,
		private readonly i18n: I18N,
		private readonly router: Router,
		private readonly aolIntegrationAPI: AOLIntegrationAPI,
		private readonly settingsState: SettingsState,
		private readonly eventAggregator: EventAggregator,
	) {
		// Get form names with subtype from AppRoutes
		this.formWithFormSubType = AppRoutes.filter(
			(route) =>
				typeof route.route === 'string' &&
				route.route.indexOf('/:' + FORM_SUBTYPE) > -1,
		).map((route) => route.route.toString().split('/:')[0]);
		Logger.log('Forms with subtype:', this.formWithFormSubType);
		this.eventAggregator.subscribeOnce(
			ReplaceConfigLabelStep.Events.ConfigLabel,
			() => {
				try {
					for (const view of Object.values(this.structure)) {
						this.transformViewData(view);
					}
				} catch (error) {
					Logger.error(error);
				}
			},
		);
	}

	public maybeFetchNavigation(view: string): Promise<void> {
		const structure = this.structure[view];
		if (structure !== undefined) {
			// NOTE: Jake: 2019-06-18
			// Don't fetch if we already have it
			return Promise.resolve();
		}
		// NOTE: Jake: 2019-06-19 - WEBUI-2040
		// In the future, can probably move the "fetchNavigationViewList" call
		// to only happen when a user opens the menu to select a different nav-view.
		// We have purposefully decoupled 'fetchNavigationViewList' and 'fetchNavigationViewStructure'
		// so that both requests can fire at the same time for concurrency.
		const fetchNavigationPromises = [];
		fetchNavigationPromises.push(
			this.navigationAPI.fetchNavigationViewList().then((navViews) => {
				if (!navViews || navViews.length === 0) {
					return Promise.reject(this.i18n.tr('navigation.no-views'));
				}
				this.viewNameList = navViews;
				return Promise.resolve();
			}),
		);
		if (view) {
			fetchNavigationPromises.push(
				this.navigationAPI
					.fetchNavigationViewStructure(view)
					.then((viewData) => {
						this.transformViewData(viewData);
						return viewData;
					})
					.then((viewData) => {
						this.structure[viewData.name] = viewData;
						return Promise.resolve();
					}),
			);
		}
		Logger.log('Retrieving navigation structure ...');
		return Promise.all(fetchNavigationPromises)
			.then(() => {
				// no-op
			})
			.catch((error) => {
				const message = FetchUtils.normalizeError(error);
				const errorMessage = this.i18n.tr(
					'navigation.unable-to-fetch',
					{
						message,
					},
				);
				Notification.error(errorMessage);
				Logger.error(errorMessage);

				// NOTE(Jae): 2020-02-24
				// Update the message on the object so we can still utilize "status"
				// in initial-load-step
				error.message = errorMessage;

				// NOTE: Jae: 2020-01-15
				// Make the error get lifted up so the "initial-load-step" pipeline can catch
				// and handle it.
				return Promise.reject(error);
			});
	}

	/**
	 * get Navigation Division View settings from navigation files
	 * If searchAllDivision is enabled, once a member is selected, switch to a configured* Navigator view
	 * associated with the member's division
	 */
	public getNavigationDivisionViewEntity(
		id: string,
		viewId: string,
		targetDivision: string,
	): string | undefined {
		const entity = this.getEntityFromID(id, viewId);
		if (
			!entity ||
			!entity.entityOptions ||
			!entity.entityOptions.searchAllDivisions ||
			!entity.entityOptions.searchAllDivisions.divisionViews
		)
			return undefined;
		for (const divisionView of entity.entityOptions.searchAllDivisions
			.divisionViews) {
			if (divisionView.viewDivision === targetDivision) {
				return divisionView.viewName;
			}
		}
		return undefined;
	}
	/**
	 * get searchAllDivision settings from navigation files
	 * If searchAllDivision is enabled,
	 * display all member accounts across all Acurity Divisions within the search bar results
	 */
	public getSearchAllDivisionStatus(id: string, viewId: string): boolean {
		const entity = this.getEntityFromID(id, viewId);
		if (
			!entity ||
			!entity.entityOptions ||
			!entity.entityOptions.searchAllDivisions ||
			!entity.entityOptions.searchAllDivisions.divisionViews
		)
			return false;
		return entity.entityOptions.searchAllDivisions.enabled;
	}

	public getEntityFromID(
		id: string | undefined | null,
		viewId: string | undefined | null,
	): NavigationEntity | undefined {
		// NOTE(Albert): 2020-03-18 - WEBUI-3223
		// viewId and id will be undefined when logging out
		// return early to avoid passing viewId to this.structure
		if (
			viewId === undefined ||
			viewId === null ||
			id === undefined ||
			id === null
		) {
			return undefined;
		}
		const view = this.structure[viewId];
		if (view === undefined || view === null) {
			return undefined;
		}
		for (const entity of view.structure.entityList) {
			if (entity.id === id) {
				return entity;
			}
		}
		return undefined;
	}

	public getQuickLinks(id: string, viewId: string): ContextMenuItem[] {
		let entityType: EntityType;
		{
			const entity = this.getEntityFromID(id, viewId);
			if (!entity) {
				return [];
			}
			entityType = entity.entityType;
		}
		const view = this.getCurrentView();
		if (!view) {
			return [];
		}
		let quickMenuList: NavigationMenuItem[] | undefined;
		for (const entity of view.structure.entityList) {
			if (
				entityType !== entity.entityType ||
				!entity.quickMenuList ||
				entity.quickMenuList.length === 0
			) {
				continue;
			}
			quickMenuList = entity.quickMenuList;
		}
		if (!quickMenuList || quickMenuList.length === 0) {
			return [];
		}
		const statefulRouteParams = filterStatefulQueryParameters(
			this.router.currentInstruction.queryParams,
		);
		const items: ContextMenuItem[] = [];
		for (const item of quickMenuList) {
			const formName = item.formName;
			const label = item.trName;
			const launchAOL = item.launchAOL;
			if (launchAOL === undefined || launchAOL === null) {
				if (!formName) {
					items.push({
						label: label + ' (Missing form name)',
						disabled: true,
						iconClass: 'fa-external-link-alt',
					});
					continue;
				}
			} else {
				// only add Launch AOL item to menu list if launchAOL is true
				// if launchAOL is defined but set to false, skip adding item
				// as the above disabled menu item is only for unexpected items
				// (i.e. items without formName or launchAOL)
				if (
					launchAOL === true &&
					(statefulRouteParams[CommonURLParam.FUND] ||
						statefulRouteParams[CommonURLParam.MEMBER] ||
						statefulRouteParams[CommonURLParam.EMPLOYER] ||
						statefulRouteParams[CommonURLParam.ADVISER])
				) {
					items.push({
						label: 'Launch AOL',
						onClick: () =>
							this.launchAOLEndpoint(statefulRouteParams),
						iconClass: 'fa-external-link-alt',
					});
				}
				continue;
			}
			const href = computeNavURL(item, statefulRouteParams);
			if (!href) {
				items.push({
					label: label + ' (Invalid form, does not exist)',
					disabled: true,
					iconClass: 'fa-external-link-alt',
				});
				continue;
			}
			items.push({
				label,
				href,
				iconClass: 'fa-external-link-alt',
			});
		}
		return items;
	}

	private launchAOLEndpoint = (params: APIParameters): void => {
		AOLIntegrationAPI.gotoAOLEndpoint(params)
			.then((data) => {
				if (data.status === httpStatus.NOT_FOUND) {
					Notification.error('Unable to locate AOL Endpoint');
					Logger.warn(data.message);
				} else {
					window.open(data);
				}
			})
			.catch(FetchUtils.logAndDisplayError);
	};

	public computeDisableDeleteForm(menuItems: NavigationMenuItem[]): boolean {
		return menuItems.some((item) => item.disableDelete);
	}

	/**
	 * computeReadOnlyForm will check if the current form has been set to readonly in the navigation JSON
	 * files.
	 */
	public computeReadOnlyForm(menuItems: NavigationMenuItem[]): boolean {
		return menuItems.some((item) => this.hasMatchWithReadOnlyGroup(item));
	}

	/**
	 * Update the formname to the path version
	 * e.g. 'my-form-subtype' -> 'my-form/subtype'
	 */
	public convertToPathSubtypeFormName(
		name: string,
		postProcess: (updated: string) => void | undefined = undefined,
	): string {
		if (name === null || name === undefined) {
			return name;
		}

		let processedMenuItemFormName = name;
		for (const formName of this.formWithFormSubType) {
			if (name.startsWith(formName + '-')) {
				processedMenuItemFormName = processedMenuItemFormName.replace(
					formName + '-',
					formName + '/',
				);
				if (postProcess) {
					postProcess(processedMenuItemFormName);
				}
				break;
			}
		}

		return processedMenuItemFormName;
	}

	/**
	 * Restore the original MenuItem form name where it has been updated to a path
	 * e.g. 'my-form/subtype' -> 'my-form-subtype'
	 */
	private restoreSubtypeFormName(name: string): string {
		if (name === null || name === undefined) {
			return name;
		}

		let processedMenuItemFormName = name;
		for (const eachFormName of this.formWithFormSubType) {
			if (processedMenuItemFormName.startsWith(eachFormName + '/')) {
				processedMenuItemFormName = processedMenuItemFormName.replace(
					'/',
					'-',
				);
				break;
			}
		}

		return processedMenuItemFormName;
	}

	private hasMatchWithReadOnlyGroup(menuItem: NavigationMenuItem): boolean {
		const userGroup = this.settingsState.state.user.userGroup.trim();
		for (const readOnlyGroup of menuItem.readOnlyGroups) {
			const readOnlyGroupValue = readOnlyGroup.rawPattern.trim();
			if (
				readOnlyGroupValue === '*' ||
				readOnlyGroupValue === userGroup
			) {
				return true;
			}
		}

		return false;
	}

	/**
	 * transformViewData will mutate the navigation view structure, giving
	 * it translated titles and handle FORM_SUBTYPE forms.
	 */
	private transformViewData(viewData: NavigationViewStructure): void {
		if (!viewData || !viewData.structure) {
			return;
		}
		for (const entity of viewData.structure.entityList) {
			for (const list of [entity.menuItemList, entity.quickMenuList]) {
				this.transformFormSubTypeToPath(list);
				this.translateTitles(list);
				NavigationState.debugValidateRoutes(list);
			}
		}
	}

	private translateTitles(menuItemList: NavigationMenuItem[]): void {
		// NOTE: Jake: 2019-10-03
		// This was copy-pasted from navigation.ts
		for (const menuItem of menuItemList) {
			menuItem.trName = computeNavigationName(menuItem);
			if (menuItem.useArchive) {
				menuItem.trName += this.i18n.tr('archive.title-suffix');
			}
			if (menuItem.childMenuItems) {
				this.translateTitles(menuItem.childMenuItems);
			}
		}
	}

	/**
	 * recursively transform formSubtype in a menu item formName to a path, e.g,
	 * supplementary-details-AA -> supplementary-details/AA
	 */
	private transformFormSubTypeToPath(
		menuItemList: NavigationMenuItem[],
	): void {
		for (const menuItem of menuItemList) {
			if (!menuItem.formName) {
				if (menuItem.childMenuItems) {
					this.transformFormSubTypeToPath(menuItem.childMenuItems);
				}
			} else {
				for (const formName of this.formWithFormSubType) {
					this.convertToPathSubtypeFormName(
						menuItem.formName,
						(updatedFormName) => {
							// NOTE: Jake: 2019-10-03 - WEBUI-2494
							// A confusing bit of behaviour occurs here.
							// It seems like the backend is automatically populating "name" with
							// "formName" if that field is empty, but only for the "menuItemList", not
							// the "quickMenuList".
							const isDefaultTranslatedFormName =
								menuItem.formName === menuItem.name;

							menuItem.formName = updatedFormName;

							if (isDefaultTranslatedFormName) {
								// NOTE: Jake: 2019-11-01 - WEBUI-2578
								// Ensure `computeNavigationName` works.
								menuItem.name = menuItem.formName;
							}
						},
					);
				}
			}
		}
	}

	private getCurrentView(): NavigationViewStructure | undefined {
		const currentView =
			this.router.currentInstruction.queryParams[CommonURLParam.NAV_VIEW];
		const view = this.structure[currentView];
		if (view === undefined || view === null) {
			return undefined;
			// NOTE(Jae): 2020-02-21
			// Old logic to just get first view.
			/* for (const viewName in this.structure) {
				if (!this.structure.hasOwnProperty(viewName)) {
					continue;
				}
				view = this.structure[viewName];
				break;
			}*/
		}
		return view;
	}

	/**
	 * Get a list of matching menuItems of current route
	 * @param viewName view to check
	 * @param formName: form name
	 * @param customisationName: customisation name
	 * @returns list of NavigationMenuItem that match current form and customisation, including child menu items
	 */
	public getCurrentMenuItems(
		viewName: string,
		formName: string,
		customisationName: string,
	): NavigationMenuItem[] | undefined {
		const currentView = this.structure[viewName];
		if (!currentView) return undefined;
		if (
			customisationName === undefined ||
			(customisationName !== null && customisationName.isBlank())
		) {
			customisationName = null; // will be null on menuitem
		}
		const menuItems = getNavigationMenuItemsOfView(currentView);
		return menuItems.filter((menuItem) => {
			return (
				menuItem.customisationName === customisationName &&
				menuItem.formName === formName
			);
		});
	}

	/**
	 * debugValidateRoutes prints a list of routes that are incorrect.
	 */
	private static debugValidateRoutes(
		menuItemList: NavigationMenuItem[],
	): void {
		if (!DEVELOPMENT) {
			return;
		}
		const invalidRoutes: NavigationMenuItem[] = [];
		NavigationState.pushInvalidRoutes(invalidRoutes, menuItemList);
		let text = '';
		for (const invalidRoute of invalidRoutes) {
			text += '- ' + invalidRoute.formName + '\n';
		}
		if (text === '') {
			return;
		}
		text =
			"Development Warning: The following routes aren't valid in your navigation JSON files:\n" +
			text;
		Logger.warn(text);
	}

	private static pushInvalidRoutes(
		invalidRouteList: NavigationMenuItem[],
		menuItemList: readonly NavigationMenuItem[],
	): void {
		for (const menuItem of menuItemList) {
			if (!menuItem.formName) {
				if (menuItem.childMenuItems) {
					NavigationState.pushInvalidRoutes(
						invalidRouteList,
						menuItem.childMenuItems,
					);
				}
				continue;
			}
			if (computeNavURL(menuItem, '') === '') {
				invalidRouteList.push(menuItem);
			}
		}
	}
}
